import { useState, useEffect, useRef } from "react";
import { Menu, X, LogOut } from "lucide-react";
import { useNavigate } from "react-router-dom";
import logo from "../assets/new-logo-wide.png";
import { useAuth } from "../providers/AuthProvider";
import { PAGE_FEATURES_UI } from "../config/featureNav";

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const navigate = useNavigate();
  const menuRef = useRef(null);
  const { signOut, can } = useAuth();

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsMenuOpen(false);
      }
    };

    if (isMenuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isMenuOpen]);

  const handleNavigation = (path) => {
    navigate(path);
    setIsMenuOpen(false);
  };

  // Nav items are derived from the feature catalog: every page feature that
  // declares nav metadata and that the user is allowed to see. Adding a feature
  // in featureNav.tsx makes it appear here automatically (desktop + mobile).
  const menuItems = PAGE_FEATURES_UI.filter(
    (f) => f.nav && can(f.perm, f.minimum)
  ).map((f) => f.nav);

  const handleLogout = async () => {
    if (window.confirm("Are you sure you want to logout?")) {
      await signOut();
      navigate("/login");
    }
  };

  return (
    <>
      <div
        className="bg-black shadow-sm border-b sticky top-0 z-40"
        ref={menuRef}
      >
        <div className="px-4 py-2 flex items-center justify-between">
          {/* Logo */}
          <button
            onClick={() => navigate("/")}
            className="h-14 md:h-14 rounded-full bg-black flex items-center justify-center hover:opacity-80 transition-opacity cursor-pointer"
            aria-label="Go to home"
          >
            <img src={logo} alt="App Logo" className="w-full h-full" />
          </button>

          {/* Desktop Navigation - Hidden on mobile, visible on md screens and up */}
          <nav className="hidden md:flex items-center gap-1">
            {menuItems.map((item, index) => {
              if (!item?.icon) return null;
              const Icon = item.icon;
              return (
                <button
                  key={item.path || index}
                  onClick={() => handleNavigation(item.path)}
                  className="px-4 py-2 flex items-center gap-2 text-white hover:bg-gray-800 rounded-md transition-colors"
                >
                  <Icon size={18} />
                  <span className="font-medium">{item.label}</span>
                </button>
              );
            })}
            <button
              onClick={() => handleLogout()}
              className="px-4 py-2 flex items-center gap-2 text-white hover:bg-gray-800 rounded-md transition-colors"
            >
              <LogOut size={18} />
              <span className="font-medium">Logout</span>
            </button>
          </nav>

          {/* Hamburger Menu Button - Visible on mobile, hidden on md screens and up */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="md:hidden text-white hover:text-gray-300 transition-colors p-2"
            aria-label="Toggle menu"
          >
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Dropdown Menu - Only shown when hamburger is clicked on mobile */}
        {isMenuOpen && (
          <div className="md:hidden absolute top-full left-0 right-0 bg-white shadow-lg border-t border-gray-200 z-50">
            <nav className="py-2">
              {menuItems.map((item, index) => {
                if (!item?.icon) return null;
                const Icon = item.icon;
                return (
                  <button
                    key={item.path || index}
                    onClick={() => handleNavigation(item.path)}
                    className="w-full px-4 py-3 flex items-center gap-3 hover:bg-gray-100 transition-colors text-left"
                  >
                    <Icon size={20} className="text-gray-600" />
                    <span className="text-gray-800 font-medium">
                      {item.label}
                    </span>
                  </button>
                );
              })}
              <button
                onClick={() => handleLogout()}
                className="w-full px-4 py-3 flex items-center gap-3 hover:bg-gray-100 transition-colors text-left"
              >
                <LogOut size={20} className="text-gray-600" />
                <span className="text-gray-800 font-medium">Logout</span>
              </button>
            </nav>
          </div>
        )}
      </div>

    </>
  );
};

export default Navbar;
